import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Box, Typography } from '@material-ui/core';

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#d32f2f',
  high: '#f57c00',
  medium: '#fbc02d',
  low: '#1976d2',
  unknown: '#757575',
  CRITICAL: '#d32f2f',
  HIGH: '#f57c00',
  MEDIUM: '#fbc02d',
  LOW: '#1976d2',
  UNKNOWN: '#757575',
};

// Cap how many bars the chart will draw. Group-by views like By CVE / Rule can
// have thousands of rows; rendering them all produces an unreadable wall of
// 1-pixel bars. Backend SQL already orders by total desc, so slicing the head
// gives the most actionable subset.
const MAX_BARS = 20;

// Render bars (and therefore the tooltip / legend order) in severity priority,
// not alphabetical. Lower index = higher priority. Keys arrive lowercase from
// the SQL aggregates, so the rank table is keyed lowercase.
const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  informational: 4,
  unknown: 5,
  none: 6,
};

const orderSeverities = (keys: string[]): string[] =>
  [...keys].sort(
    (a, b) =>
      (SEVERITY_RANK[a.toLowerCase()] ?? 99) -
      (SEVERITY_RANK[b.toLowerCase()] ?? 99),
  );

interface SeverityBarChartProps {
  data: Array<Record<string, any>>;
  nameKey: string;
  severityKeys: string[];
  height?: number;
  /**
   * When set, clicking any segment of a bar (or the bar's axis tick area)
   * invokes this with the row's `nameKey` value — same drill-down semantics
   * as a SummaryTable row click.
   */
  onBarClick?: (name: string) => void;
  /**
   * Formats the raw `nameKey` value for the x-axis tick and tooltip header
   * only — `onBarClick`/`activeLabel` still receive the raw value, so
   * drill-down codes are unaffected.
   */
  tickFormatter?: (name: string) => string;
}

export const SeverityBarChart = ({ data, nameKey, severityKeys, height = 400, onBarClick, tickFormatter }: SeverityBarChartProps) => {
  const truncated = data.length > MAX_BARS;
  const chartData = truncated ? data.slice(0, MAX_BARS) : data;
  const orderedSeverityKeys = orderSeverities(severityKeys);
  const handleClick = onBarClick
    ? (state: any) => {
        const label = state?.activeLabel;
        if (label !== undefined && label !== null && label !== '') {
          onBarClick(String(label));
        }
      }
    : undefined;
  return (
    <Box>
      {truncated && (
        <Box mb={1}>
          <Typography variant="caption" color="textSecondary">
            Showing top {MAX_BARS} of {data.length.toLocaleString()} (by total, descending). Full breakdown is in the table above.
          </Typography>
        </Box>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          onClick={handleClick}
          style={onBarClick ? { cursor: 'pointer' } : undefined}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={nameKey}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            fontSize={11}
            tickFormatter={tickFormatter}
          />
          <YAxis />
          <Tooltip
            labelFormatter={
              tickFormatter && (label => tickFormatter(String(label)))
            }
            itemSorter={item =>
              SEVERITY_RANK[String(item.dataKey).toLowerCase()] ?? 99
            }
          />
          <Legend verticalAlign="top" />
          {orderedSeverityKeys.map(key => (
            <Bar key={key} dataKey={key} stackId="severity" fill={SEVERITY_COLORS[key] || '#9e9e9e'} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

interface StackedTrendChartProps {
  data: Array<{ date: string; opened_vulns: number; resolved_vulns: number }>;
  height?: number;
}

export const StackedTrendChart = ({ data, height = 400 }: StackedTrendChartProps) => {
  // Aggregate by date
  const byDate: Record<string, { date: string; opened: number; resolved: number }> = {};
  for (const row of data) {
    const d = row.date;
    if (!byDate[d]) byDate[d] = { date: d, opened: 0, resolved: 0 };
    byDate[d].opened += row.opened_vulns;
    byDate[d].resolved += Math.abs(row.resolved_vulns);
  }
  const chartData = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" angle={-45} textAnchor="end" height={70} fontSize={10} />
        <YAxis />
        <Tooltip />
        <Legend verticalAlign="top" />
        <Bar dataKey="opened" fill="#f57c00" name="Opened" />
        <Bar dataKey="resolved" fill="#4caf50" name="Resolved" />
      </BarChart>
    </ResponsiveContainer>
  );
};
